<script setup lang="ts">
// The member's own view of the map (doc/in-progress/site-tagging-and-symbology.md
// §8.2): the fourth tab of the Map Review rail, available to every member
// without an admin role, because an override is one person's view and nothing
// here writes the district's vocabulary.
//
// The pane is a layout of the resolution order, not a re-statement of it. The
// groups appear in the order the order applies them — tags (arms 1–2) above
// subject kinds (arms 3–5) — and every badge is the catalog's own
// `style.source`, so a row cannot disagree with what the map draws. The pane
// never decides that a style came from the district: the server resolved it and
// said so (§4.2/§4.5).
//
// Nothing here is a warning dialog. Every write is the picker itself; the two
// precedence notices exist to explain the order, and the third (on the tag rows
// a member's kind override cannot reach) keeps the row and the map agreeing
// instead of letting an operator read "my kind style does nothing" as a bug.
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ToastNotification from "@/components/ToastNotification.vue";
import { styleCss } from "@/lib/symbology";
import type {
	SiteTagUsage,
	Symbol,
	SymbolStyle,
	SymbolTagStyle,
	SymbologyCatalog,
	SymbologyKind,
} from "@/type/api";

/** One row of GET /api/symbology/override: the caller's own overrides, which is
 * all this pane needs beside the catalog — the pickers' current value, and the
 * two notices that fire on "the member has a kind override". */
interface SymbolOverride {
	id: number;
	tag_id: number | null;
	subject_kind: string | null;
	symbol_id: number;
	symbol_name: string;
}

interface Props {
	/** The session's resolved catalog. Null until the first read lands: the
	 * pane then says it is loading rather than rendering an empty vocabulary
	 * that looks like a district with no tags. */
	catalog: SymbologyCatalog | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ refresh: [] }>();

/** The badge's word for one resolved arm. This is the catalog's `source`
 * spelled, never a second derivation of the precedence order. */
const SOURCE_LABEL: Record<SymbolStyle["source"], string> = {
	user: "yours",
	district: "district",
	builtin: "built-in",
};

/** What a tag with no district binding is labelled (§5.3). `builtin` on a tag
 * row is exactly that: the tag arms bottom out in the product default because
 * neither the member nor the district has bound a symbol to this tag, so the
 * tile never emits it and the override — if there is one — can never fire. The
 * badge says so instead of pretending the tag draws. */
const NO_BINDING_LABEL = "no district binding — not shown on the map";

/** The taggable kind. The tag arms are consulted for this kind only in v1
 * (§9.4), and it is the kind the two Site-specific notices speak about. */
const SITE_KIND = "site";

const overrides = ref<SymbolOverride[]>([]);
const overridesError = ref("");

const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

/** The write in flight, keyed `tag:<id>` or `kind:<code>`, so the row that is
 * waiting is the only one disabled. */
const busy = ref<string | null>(null);

const tags = computed<SymbolTagStyle[]>(() => props.catalog?.tags ?? []);
const kinds = computed<SymbologyKind[]>(() => props.catalog?.kinds ?? []);

/** The symbols a picker may offer: the catalog carries every visible symbol,
 * retired ones included because a retired symbol that is still bound keeps
 * drawing (§5.3), but this is a picker — the library's retired rows are not
 * choices. */
const districtSymbols = computed<Symbol[]>(() =>
	(props.catalog?.symbols ?? []).filter(
		(symbol) => !symbol.retired && symbol.owner === "district",
	),
);
const privateSymbols = computed<Symbol[]>(() =>
	(props.catalog?.symbols ?? []).filter(
		(symbol) => !symbol.retired && symbol.owner === "user",
	),
);

/** §8.2 notice 1: the district styles at least one tag, so the kind defaults
 * below cannot reach Sites carrying it. Derived from the catalog's own sources,
 * never from the binding list — a binding the member cannot see is still a
 * binding. */
const districtStylesATag = computed<boolean>(() =>
	tags.value.some((tag) => tag.style.source === "district"),
);

/** Whether the member has overridden the site kind at all. It is the other half
 * of notice 3: with no kind override there is no style of theirs to be ignored. */
const siteKindOverridden = computed<boolean>(() =>
	overrides.value.some((override) => override.subject_kind === SITE_KIND),
);

function tagOverride(tagID: number): SymbolOverride | undefined {
	return overrides.value.find((override) => override.tag_id === tagID);
}

function kindOverride(code: string): SymbolOverride | undefined {
	return overrides.value.find((override) => override.subject_kind === code);
}

/** The site kind's resolved default, for the legend's last line ("Sites with no
 * tag"), or null when the catalog does not carry the kind. */
const siteKindDefault = computed<SymbolStyle | null>(
	() => kinds.value.find((kind) => kind.code === SITE_KIND)?.default ?? null,
);

/** The district's Site split, read once for the notice-2 figure. It is
 * optional on purpose: the count explains what a kind override reaches, it is
 * not the override, so a failed or partial answer leaves the sentence standing
 * without its number rather than holding the pane. */
const usage = ref<SiteTagUsage | null>(null);

/** `sites_untagged` is exactly the set a kind default reaches: the ranked tag
 * set (§4.1) carries only district-bound tags, so a Site whose every tag is
 * unbound has no winner and is counted here. */
const untaggedSites = computed<number | null>(
	() => usage.value?.sites_untagged ?? null,
);

/** Which subject one write is about. The override endpoints are one family with
 * two address shapes — a tag id and a kind code — so the pane states it once and
 * builds both the key and the path from it. */
type Subject = { tag: number } | { kind: string };

async function loadOverrides(): Promise<void> {
	try {
		overrides.value = await apiClient.JSONGet<SymbolOverride[]>(
			"/api/symbology/override",
		);
		overridesError.value = "";
	} catch (e: any) {
		overridesError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to read your symbology overrides.";
	}
}

/** The usage read is deliberately quiet: it has no error line and raises no
 * toast, because the only thing it feeds is a parenthetical figure. A district
 * whose read fails still gets the sentence that explains the precedence. */
async function loadUsage(): Promise<void> {
	try {
		usage.value = await apiClient.JSONGet<SiteTagUsage>("/api/site-tag/usage");
	} catch {
		usage.value = null;
	}
}

/**
 * One override write, then the two reads that keep every surface honest: the
 * pane's own override list (the notices and the pickers read it) and the
 * catalog (the paint and the badges read it). The catalog reload is the view's
 * — it owns the copy the map paints from — so the pane asks for it through
 * `refresh` rather than reaching into the module.
 *
 * A refusal is shown in the server's words: an override on a symbol the member
 * may not see, or on a tag of another district, arrives as a sentence an
 * operator can act on, and paraphrasing it would lose the reason.
 */
async function writeOverride(
	subject: Subject,
	symbolID: number | null,
): Promise<void> {
	const key = "tag" in subject ? `tag:${subject.tag}` : `kind:${subject.kind}`;
	const url =
		"tag" in subject
			? `/api/symbology/override/tag/${subject.tag}`
			: `/api/symbology/override/kind/${subject.kind}`;
	busy.value = key;
	try {
		if (symbolID === null) await apiClient.JSONDelete(url);
		else await apiClient.JSONPut(url, { symbol_id: symbolID });
		await loadOverrides();
		emit("refresh");
	} catch (e: any) {
		toastTitle.value =
			symbolID === null
				? "Could not reset the style"
				: "Could not save the style";
		toastMessage.value =
			e?.response?.data?.message ||
			e?.message ||
			"The server did not accept the change.";
		toastShow.value = true;
	} finally {
		busy.value = null;
	}
}

/** The symbol a picker's change names, or null for the select's first option
 * ("use the resolved style"). Undefined is a value that names no symbol at all —
 * a stale DOM — and writes nothing. */
function pickedSymbol(event: Event): number | null | undefined {
	const raw = (event.target as HTMLSelectElement).value;
	if (raw === "") return null;
	const symbolID = Number(raw);
	return Number.isInteger(symbolID) ? symbolID : undefined;
}

/** A picker's change, or the row's Reset when `event` is null. Both are the
 * same write: the override in force, or none. */
function onPick(subject: Subject, event: Event | null): void {
	const symbolID = event === null ? null : pickedSymbol(event);
	if (symbolID === undefined) return;
	void writeOverride(subject, symbolID);
}

onMounted(() => {
	void loadOverrides();
	void loadUsage();
});
</script>

<template>
	<div class="control-pane symbology-pane">
		<p v-if="overridesError" class="symbology-error">{{ overridesError }}</p>
		<p v-if="catalog === null" class="symbology-empty">
			Loading the district's symbology…
		</p>

		<template v-else>
			<!-- Group 1 — tags (arms 1–2), in the catalog's own order (priority,
			     the rank the tile uses). Each row's badge is the resolved arm; a
			     tag the district has not bound is listed anyway and badged as
			     inert, because an override on it is stored and listed but can
			     never reach the map (§5.3). -->
			<div class="symbology-group">
				<div class="control-pane-tool-label">Tags</div>
				<p class="symbology-help">
					Your own symbol for a tag wins over the district's on your map only.
				</p>
				<p v-if="tags.length === 0" class="symbology-empty">
					The district has no tags yet.
				</p>
				<div v-for="tag in tags" :key="tag.tag_id" class="symbology-row">
					<div class="symbology-row-main">
						<span
							class="symbology-swatch"
							:style="styleCss(tag.style)"
							aria-hidden="true"
						></span>
						<span class="symbology-name">
							{{ tag.name }}
							<span v-if="tag.retired" class="symbology-retired">
								retired
							</span>
						</span>
						<span
							class="symbology-badge"
							:class="{
								'symbology-badge--yours': tag.style.source === 'user',
								'symbology-badge--unbound': tag.style.source === 'builtin',
							}"
							:title="
								tag.style.name
									? `Drawn with ${tag.style.name}`
									: 'Drawn with the product default'
							"
						>
							{{
								tag.style.source === "builtin"
									? NO_BINDING_LABEL
									: SOURCE_LABEL[tag.style.source]
							}}
						</span>
					</div>
					<div class="symbology-row-controls">
						<select
							class="symbology-select"
							:value="tagOverride(tag.tag_id)?.symbol_id ?? ''"
							:disabled="busy === `tag:${tag.tag_id}`"
							:aria-label="`Symbol for the tag ${tag.name}`"
							:title="`What your map draws a Site carrying ${tag.name} with`"
							@change="onPick({ tag: tag.tag_id }, $event)"
						>
							<option value="">
								Use the resolved style —
								{{ tag.style.name ?? "product default" }}
							</option>
							<optgroup
								v-if="districtSymbols.length > 0"
								label="District library"
							>
								<option
									v-for="symbol in districtSymbols"
									:key="symbol.id"
									:value="symbol.id"
								>
									{{ symbol.name }}
								</option>
							</optgroup>
							<optgroup
								v-if="privateSymbols.length > 0"
								label="Your private symbols"
							>
								<option
									v-for="symbol in privateSymbols"
									:key="symbol.id"
									:value="symbol.id"
								>
									{{ symbol.name }}
								</option>
							</optgroup>
						</select>
						<button
							type="button"
							class="control-pane-btn"
							:disabled="
								busy === `tag:${tag.tag_id}` || !tagOverride(tag.tag_id)
							"
							:title="
								tagOverride(tag.tag_id)
									? `Drop your own symbol for ${tag.name}`
									: 'You have no personal symbol on this tag'
							"
							@click="onPick({ tag: tag.tag_id }, null)"
						>
							Reset
						</button>
					</div>
					<!-- Notice 3 (§8.2): this member's kind override exists and the
					     district binds this tag, so the district's tag style — the
					     badge above — is what the map draws. The row says why, so the
					     member reads it as precedence rather than as a broken
					     override. -->
					<p
						v-if="tag.style.source === 'district' && siteKindOverridden"
						class="symbology-note"
					>
						Your kind style does not apply here; the district's tag style wins.
					</p>
				</div>
			</div>

			<!-- Group 2 — subject kinds (arms 3–5). The notice is derived from the
			     catalog's sources: a district-styled tag exists, so these defaults
			     are not what a Site carrying it draws. -->
			<div class="symbology-group">
				<div class="control-pane-tool-label">Subject kinds</div>
				<p v-if="districtStylesATag" class="symbology-notice">
					District tag styling takes precedence over these defaults. Sites
					carrying a district-styled tag keep the district's symbol — to change
					one, override that tag above.
				</p>
				<div v-for="kind in kinds" :key="kind.code" class="symbology-row">
					<div class="symbology-row-main">
						<span
							class="symbology-swatch"
							:style="styleCss(kind.default)"
							aria-hidden="true"
						></span>
						<span class="symbology-name">{{ kind.label }}</span>
						<span
							class="symbology-badge"
							:class="{
								'symbology-badge--yours': kind.default.source === 'user',
							}"
							:title="
								kind.default.name
									? `Drawn with ${kind.default.name}`
									: 'Drawn with the product default'
							"
						>
							{{ SOURCE_LABEL[kind.default.source] }}
						</span>
					</div>
					<div class="symbology-row-controls">
						<select
							class="symbology-select"
							:value="kindOverride(kind.code)?.symbol_id ?? ''"
							:disabled="busy === `kind:${kind.code}`"
							:aria-label="`Default symbol for ${kind.label}`"
							:title="`What your map draws a ${kind.label} with when no tag decides it`"
							@change="onPick({ kind: kind.code }, $event)"
						>
							<option value="">
								Use the resolved default —
								{{ kind.default.name ?? "product default" }}
							</option>
							<optgroup
								v-if="districtSymbols.length > 0"
								label="District library"
							>
								<option
									v-for="symbol in districtSymbols"
									:key="symbol.id"
									:value="symbol.id"
								>
									{{ symbol.name }}
								</option>
							</optgroup>
							<optgroup
								v-if="privateSymbols.length > 0"
								label="Your private symbols"
							>
								<option
									v-for="symbol in privateSymbols"
									:key="symbol.id"
									:value="symbol.id"
								>
									{{ symbol.name }}
								</option>
							</optgroup>
						</select>
						<button
							type="button"
							class="control-pane-btn"
							:disabled="
								busy === `kind:${kind.code}` || !kindOverride(kind.code)
							"
							:title="
								kindOverride(kind.code)
									? `Drop your own default for ${kind.label}`
									: 'You have no personal default on this kind'
							"
							@click="onPick({ kind: kind.code }, null)"
						>
							Reset
						</button>
					</div>
					<!-- The suffix §8.2 requires on an overridden kind row: what the
					     override actually reaches. It is stated where the tag arms are
					     consulted at all — a non-taggable kind has no tag styling to
					     yield to, so the Site wording would be false there. The figure
					     is `sites_untagged` from GET /api/site-tag/usage: the Sites no
					     district-styled tag ranks on, which is precisely the set this
					     default draws. A district whose usage read has not answered
					     keeps the sentence without it. -->
					<p
						v-if="kind.taggable && kindOverride(kind.code)"
						class="symbology-note"
					>
						Applies to untagged Sites and to tags the district has not
						styled<template v-if="untaggedSites !== null">
							({{ untaggedSites.toLocaleString() }} sites)</template
						>.
					</p>
				</div>
			</div>

			<!-- The legend: the same tag styles, so the pane doubles as the map's
			     key. The last line is the fallback a Site with no ranked tag
			     draws with — the site kind's own default branch. -->
			<div class="symbology-group">
				<div class="control-pane-tool-label">Legend</div>
				<div
					v-for="tag in tags"
					:key="`legend-${tag.tag_id}`"
					class="symbology-legend-row"
				>
					<span
						class="symbology-swatch"
						:style="styleCss(tag.style)"
						aria-hidden="true"
					></span>
					<span class="symbology-name">{{ tag.name }}</span>
				</div>
				<div v-if="siteKindDefault" class="symbology-legend-row">
					<span
						class="symbology-swatch"
						:style="styleCss(siteKindDefault)"
						aria-hidden="true"
					></span>
					<span class="symbology-name symbology-name--muted">
						Sites with no tag
					</span>
				</div>
				<p v-if="tags.length === 0" class="symbology-empty">
					Nothing to key yet.
				</p>
			</div>
		</template>

		<ToastNotification
			v-model:show="toastShow"
			:title="toastTitle"
			:message="toastMessage"
		/>
	</div>
</template>

<style scoped>
/* The rail body owns the box — width, background, padding and scroll — so the
   pane is only its content and its type scale. The `.control-pane-*` names are
   the rail panes' own, re-declared here because a scoped rule never reaches
   into another component's markup. */
.control-pane {
	font-size: 0.8125rem;
}

.control-pane-tool-label {
	font-weight: 600;
	color: #495057;
}

.symbology-group + .symbology-group {
	margin-top: 12px;
	padding-top: 10px;
	border-top: 1px solid #e9ecef;
}

.symbology-help {
	margin: 2px 0 6px;
	color: #868e96;
	font-size: 0.75rem;
	line-height: 1.35;
}

/* The precedence notice: a quiet block, not a warning. The order is the map's
   behaviour, so it reads as an explanation of what is on screen. */
.symbology-notice {
	margin: 4px 0 8px;
	padding: 6px 8px;
	border-left: 3px solid #74c0fc;
	background: #f8f9fa;
	color: #495057;
	font-size: 0.75rem;
	line-height: 1.35;
}

.symbology-row {
	padding: 5px 0;
	border-bottom: 1px solid #f1f3f5;
}

.symbology-row:last-child {
	border-bottom: none;
}

.symbology-row-main {
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
}

/* The style preview is a swatch of the resolved arm, drawn through styleCss:
   the fill as the background, the outline as the border, each at its own
   opacity — the two channels the map paints. */
.symbology-swatch {
	flex: 0 0 auto;
	width: 18px;
	height: 18px;
	border-radius: 3px;
	box-sizing: border-box;
}

.symbology-name {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: #212529;
}

.symbology-name--muted {
	color: #868e96;
	font-style: italic;
}

.symbology-retired {
	margin-left: 4px;
	color: #868e96;
	font-size: 0.6875rem;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

/* The badge is the catalog's `source`, so a member can see at a glance whether
   the row is their own style, the district's, or the product default. */
.symbology-badge {
	flex: 0 0 auto;
	padding: 1px 5px;
	border-radius: 999px;
	background: #e9ecef;
	color: #495057;
	font-size: 0.6875rem;
	line-height: 1.35;
	white-space: nowrap;
}

.symbology-badge--yours {
	background: #d0ebff;
	color: #1864ab;
}

/* An unbound tag is not a style the map draws; the badge says so plainly rather
   than letting the empty-looking row read as the tag's colour. */
.symbology-badge--unbound {
	background: #fff3bf;
	color: #856404;
	white-space: normal;
	text-align: right;
}

.symbology-row-controls {
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 4px;
}

.symbology-select {
	flex: 1 1 auto;
	min-width: 0;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 2px 4px;
	font-size: 0.75rem;
	background: #fff;
	color: #212529;
}

.symbology-select:disabled {
	opacity: 0.55;
}

.control-pane-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
}

.control-pane-btn:hover:not(:disabled) {
	background: #f1f3f5;
}

.control-pane-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

/* The quiet row suffix: why this row's style is not the one the member just
   chose. Small enough to be read as a footnote to the row it belongs to. */
.symbology-note {
	margin: 3px 0 0;
	color: #868e96;
	font-size: 0.6875rem;
	line-height: 1.35;
}

.symbology-legend-row {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 3px 0;
}

.symbology-empty {
	margin: 4px 0 0;
	color: #868e96;
	font-size: 0.75rem;
}

.symbology-error {
	margin: 0 0 6px;
	color: #c92a2a;
	font-size: 0.75rem;
}
</style>
